import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Emergent Google Auth callback landing page.
// Reads `session_id` from the URL fragment (Emergent puts it in the hash, not
// the query, so it never hits the server), POSTs it to our backend to
// establish a first-party session cookie, then routes into the app.
export default function AuthCallback() {
  const nav = useNavigate();
  const { setSessionUser } = useAuth();
  const ranRef = useRef(false); // Guard against React StrictMode double-mount.

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("Missing Google session. Please try signing in again.");
      nav("/login", { replace: true });
      return;
    }

    // Strip the fragment from the URL immediately so a page refresh doesn't
    // re-submit the (now-consumed) one-time session_id.
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch {}

    api
      .post("/auth/google/session", null, { headers: { "X-Session-ID": sessionId } })
      .then(({ data }) => {
        setSessionUser(data.user);
        toast.success(`Welcome, ${data.user.username || "friend"}!`);
        nav("/discover", { replace: true });
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed.");
        nav("/login", { replace: true });
      });
  }, [nav, setSessionUser]);

  return (
    <div className="flex min-h-screen items-center justify-center" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-neutral-300">
        <Loader2 size={32} className="animate-spin text-violet-300" />
        <div className="font-display text-lg text-white">Finishing sign-in…</div>
        <div className="text-xs text-neutral-500">One moment.</div>
      </div>
    </div>
  );
}
